"use client";
/**
 * Full-height navigation sheet for phone / tablet. Hover dropdowns and a
 * wrapping 20-tab top bar are unusable on iOS; this is the tap surface.
 *
 * Same tree as TopNav (visible items only). Children expand in place —
 * no hover. Closes on navigation, Escape, or backdrop tap.
 */
import { useEffect, useState, type ReactNode } from "react";
import Link from "@/lib/spa/Link";
import { Logo } from "@/components/ui/Logo";
import { hrefOf, type NavItem } from "@/lib/nav/navConfig";

export function MobileNavDrawer({
  open,
  onClose,
  items,
  activeTopId,
  activeChildId,
  headerExtra,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  activeTopId: string;
  activeChildId: string;
  headerExtra?: ReactNode;
  footer?: ReactNode;
}) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(activeTopId ? [activeTopId] : []));

  useEffect(() => {
    if (!open) return;
    setOpenGroups((cur) => (activeTopId && !cur.has(activeTopId) ? new Set([...cur, activeTopId]) : cur));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, activeTopId, onClose]);

  if (!open) return null;

  const row = (active: boolean) =>
    `flex min-h-11 w-full items-center rounded-lg px-3 text-[15px] font-medium transition ${
      active ? "bg-accent-soft text-accent-ink" : "text-ink hover:bg-surface-sunken"
    }`;

  const targetOf = (it: NavItem, className: string, children: ReactNode, onClick?: () => void) => {
    const target = hrefOf(it);
    if (it.kind === "link" && it.external && target) {
      return (
        <a key={it.id} href={target} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
          {children}
        </a>
      );
    }
    return (
      <Link key={it.id} href={target ?? "#"} prefetch className={className} onClick={onClick}>
        {children}
      </Link>
    );
  };

  return (
    <div className="mpp-compact-only fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Navigation">
      <button type="button" className="absolute inset-0 bg-ink/40" aria-label="Close menu" onClick={onClose} />
      <nav
        className="absolute inset-y-0 left-0 flex w-[min(22rem,100%)] flex-col border-r border-line bg-surface shadow-panel-raised"
        style={{ paddingTop: "var(--mpp-sat)", paddingBottom: "var(--mpp-sab)" }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
          <Link href="/dashboard" prefetch aria-label="Afiat MarketPulse Pro home" className="text-ink" onClick={onClose}>
            <Logo size={28} />
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="grid h-11 w-11 place-items-center rounded-md text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {headerExtra ? <div className="border-b border-line px-3 py-2">{headerExtra}</div> : null}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
          {items.map((p) => {
            const kids = (p.children ?? []).filter((c) => c.visible);
            const active = p.id === activeTopId;
            if (kids.length === 0) {
              return targetOf(p, row(active), p.label, onClose);
            }
            const expanded = openGroups.has(p.id);
            return (
              <div key={p.id} className="mb-0.5">
                <div className="flex items-stretch gap-0.5">
                  {targetOf(p, `${row(active)} flex-1`, p.label, onClose)}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? "Collapse" : "Expand"} ${p.label}`}
                    onClick={() =>
                      setOpenGroups((cur) => {
                        const next = new Set(cur);
                        if (next.has(p.id)) next.delete(p.id);
                        else next.add(p.id);
                        return next;
                      })
                    }
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
                  >
                    <svg
                      className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
                {expanded ? (
                  <div className="ml-3 border-l border-line pl-2">
                    {kids.map((c) =>
                      targetOf(c, `${row(c.id === activeChildId)} text-[14px]`, c.label, onClose),
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {footer ? <div className="border-t border-line px-3 py-2">{footer}</div> : null}
      </nav>
    </div>
  );
}
